import { ApplicationState } from ".."
import { EntitiesData,
    EntityType,
    Entity,
    AppointmentEntity,
    ClientEntity,
    ProfessionalEntity,
    UserEntity,
    ReportEntity,
    ChangeType,
    EntityChange,
    ClientPackEntity,
    FileEntity,
    CommunicationEntity,
    PaymentEntity,
} from "./state";
import { ChangeLog } from "../../models";

export const getEntitiesData = <T extends Entity>(type: EntityType) => (state: ApplicationState): EntitiesData<T> => state.entities[type] as unknown as EntitiesData<T>;
export const getEntitiesSearchItems = <T extends Entity>(state: ApplicationState, type: EntityType): T[] => state.entities[type].searchResult.map(id => state.entities[type].items[id] as unknown as T).filter(v => !!v);
export const getEntitiesHistoryItems = <T extends Entity>(state: ApplicationState, type: EntityType): T[] => state.entities[type].changingHistory.map(id => state.entities[type].items[id] as unknown as T).filter(v => !!v);
export const getEntitiesDataItems = <T extends Entity>(type: EntityType) => (state: ApplicationState): T[] => Object.values(getEntitiesData<T>(type)(state).items).filter(e => e);
export const isLoadingEntities = (type: EntityType) => (state: ApplicationState): boolean => state.entities[type].isLoading;
export const isSubmitting = (type: EntityType) => (state: ApplicationState): boolean =>  state.entities[type].changing?.saving ?? false;
export const isSubmittingNew = (state: ApplicationState, type: EntityType): boolean => state.entities[type].changing?.action === 'creating' && (state.entities[type].changing?.saving ?? false);
export const getEntity = <T extends Entity>(state: ApplicationState, type: EntityType, id: string): T | undefined => state.entities[type].items[id]  as unknown as T;
export const getChangeLog = (state: ApplicationState, type: EntityType, id: string): ChangeLog[] => state.entities[type]?.changeLogs[id] || [];
export const getChangingType = (state: ApplicationState, type: EntityType): ChangeType | undefined => state.entities[type]?.changing?.action;
export const getChangingValue = (state: ApplicationState, type: EntityType): Entity | undefined => state.entities[type]?.changing?.value;
export const getChangingState = (state: ApplicationState, type: EntityType): boolean => state.entities[type]?.changing?.hasChanged ?? false;

// Client
export const getEntitiesDataClient = (state: ApplicationState) => getEntitiesData<ClientEntity>('clients')(state)

interface Changings extends Record<EntityType, EntityChange<any>|undefined>{
    users: EntityChange<UserEntity> | undefined,
    clients: EntityChange<ClientEntity> | undefined,
    professionals: EntityChange<ProfessionalEntity> | undefined,
    appointments: EntityChange<AppointmentEntity> | undefined, 
    reports: EntityChange<ReportEntity> | undefined,
    clientPacks: EntityChange<ClientPackEntity> | undefined,
    files: EntityChange<FileEntity> | undefined,
    communications: EntityChange<CommunicationEntity> | undefined,
    payments: EntityChange<PaymentEntity> | undefined,
}
export const getEntitiesBeingChanged = (state: ApplicationState): Changings => ({
    users: state.entities.users.changing,
    clients: state.entities.clients.changing,
    professionals: state.entities.professionals.changing,
    appointments: state.entities.appointments.changing,
    reports: state.entities.reports.changing,
    clientPacks: state.entities.clientPacks.changing,
    files: state.entities.files.changing,
    communications: state.entities.communications.changing,
    payments: state.entities.payments.changing,
    changeLogs: state.entities.changeLogs.changing,
    companies: state.entities.companies.changing,
    expenses: state.entities.expenses.changing,
    stock: state.entities.stock.changing,
    orders: state.entities.orders.changing,
    prescriptions: state.entities.prescriptions.changing,
});

export const getClient = (clientId: string) => (state: ApplicationState): ClientEntity | undefined => (state.entities['clients'].items[clientId]) || undefined; 
export const getReportsForClient = (clientId: string) => (state: ApplicationState): ReportEntity[] => 
    (Object.values(state.entities['reports'].items)?.filter(r => r?.clientId === clientId)) || [];
export const getFilesForClient = (clientId: string) => (state: ApplicationState): FileEntity[] => 
    (Object.values(state.entities['files'].items)?.filter(r => r?.clientId === clientId)) || [];
export const getPacksForClient = (clientId: string) => (state: ApplicationState): ClientPackEntity[] => 
    (Object.values(state.entities['clientPacks'].items)?.filter(r => r?.clientId === clientId && !r.clientIsProfessional)) || [];
export const getPacksForProfessional = (professionalId: string) => (state: ApplicationState): ClientPackEntity[] => 
    (Object.values(state.entities['clientPacks'].items)?.filter(r => r?.clientId === professionalId && r.clientIsProfessional)) || [];
export const getAppointmentsForClient = (id: string) => (state: ApplicationState): AppointmentEntity[] => !id ? [] :
    getEntitiesDataItems<AppointmentEntity>('appointments')(state).filter(e => e?.clientId === id);
export const getAppointmentsForProfessional = (id: string) => (state: ApplicationState): AppointmentEntity[] => !id ? [] :
    getEntitiesDataItems<AppointmentEntity>('appointments')(state)
        .filter(e => e?.professionalId === id || e?.additionalProfessionals?.some(p => p.id === id))
        .sort((a, b) => a.date > b.date ? -1 : 1);
export const getAppointmentsOfCurrentPack = (state: ApplicationState): Record<number, AppointmentEntity> => {
    const pack = getChangingValue(state, 'clientPacks') as ClientPackEntity|undefined
    if(!pack?.appointments) return []
    return Object.fromEntries(pack.appointments
        .map(appointmentId => getEntity(state, 'appointments', appointmentId))
        .filter((appointment): appointment is AppointmentEntity => !!appointment)
        .map(appointment => [appointment.id, appointment]))
}
export const getPrescriptionsByClient = (clientId: string) => (state: ApplicationState) =>
    (Object.values(state.entities['prescriptions'].items)?.filter(r => r?.clientId === clientId)) || [];
